import { Skeleton, Spin } from 'antd';
import styled from 'styled-components';

/**
 * Estado de carregamento padrão do sistema (UX-050 da auditoria).
 *
 * Existe para acabar com os ~15 `<Spin style={{ display:'block', margin:'80px auto' }}>`
 * inline: cada tela com uma margem diferente (80px, 48, 40), nenhum com texto
 * para leitor de tela, e todos "pulando" de altura entre si.
 *
 * REGRA DE ESCOLHA (§8.10 do design system): em tabela, lista e card, prefira
 * SEMPRE o esqueleto ao spinner: ele preserva o layout e não pisca. Spinner só
 * quando não há forma conhecida para desenhar, ou em ação bloqueante curta.
 *
 * ```tsx
 * // conteúdo da página inteira (detalhe, formulário) enquanto carrega
 * <Carregando variante="pagina" texto="Carregando turma..." />
 *
 * // dentro de um card ou seção
 * <Carregando texto="Carregando notas..." />
 *
 * // preferido para listas/tabelas e cards: nada de spinner central
 * <Carregando esqueleto="tabela" linhas={5} />
 * <Carregando esqueleto="formulario" linhas={6} />
 * <Carregando esqueleto="cartao" />
 *
 * // ação bloqueante < 2s cobrindo a tela (acima disso, use progresso com texto)
 * <Carregando variante="tela" texto="Salvando matrícula..." />
 * ```
 *
 * ACESSIBILIDADE: o contêiner é `role="status" aria-live="polite" aria-busy`, e
 * o `texto` é lido pelo leitor de tela. Por isso `texto` deve descrever O QUE
 * carrega ("Carregando turmas..."), não apenas "Carregando...".
 *
 * NOTA: `<Table loading>` do AntD já resolve o carregamento de tabelas montadas;
 * este componente é para o primeiro render, detalhe, formulário e abas.
 */

/** `bloco` (padrão) dentro de card/seção · `pagina` área de conteúdo · `tela` overlay fixo. */
export type VarianteCarregando = 'bloco' | 'pagina' | 'tela';

/** Forma do esqueleto. Quando informado, substitui o spinner. */
export type EsqueletoCarregando = 'tabela' | 'formulario' | 'cartao';

export interface CarregandoProps {
  variante?: VarianteCarregando;
  /** Descrição do que está sendo carregado. Lido por leitor de tela. */
  texto?: string;
  /** Desenha um esqueleto no lugar do spinner, preferido em tabela/card. */
  esqueleto?: EsqueletoCarregando;
  /** Nº de linhas/campos do esqueleto. Padrão: 5 (tabela) / 4 (formulário). */
  linhas?: number;
  className?: string;
  /** @deprecated Use `variante="tela"`. Mantido pelos consumidores existentes. */
  fullscreen?: boolean;
}

export function Carregando({
  variante,
  texto = 'Carregando...',
  esqueleto,
  linhas,
  className,
  fullscreen,
}: CarregandoProps) {
  const varianteEfetiva: VarianteCarregando = variante ?? (fullscreen ? 'tela' : 'bloco');

  const conteudo = esqueleto ? (
    <Esqueleto tipo={esqueleto} linhas={linhas} />
  ) : (
    <>
      <Spin size={varianteEfetiva === 'bloco' ? 'medium' : 'large'} />
      {texto && <Texto>{texto}</Texto>}
    </>
  );

  return (
    <Container
      className={className}
      $variante={varianteEfetiva}
      $esqueleto={Boolean(esqueleto)}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={esqueleto ? texto : undefined}
    >
      {conteudo}
    </Container>
  );
}

export default Carregando;

/* ------------------------------------------------------------------ */
/* Esqueletos                                                           */
/* ------------------------------------------------------------------ */

function Esqueleto({ tipo, linhas }: { tipo: EsqueletoCarregando; linhas?: number }) {
  if (tipo === 'cartao') {
    return <Skeleton active avatar paragraph={{ rows: linhas ?? 3 }} />;
  }

  if (tipo === 'formulario') {
    const total = linhas ?? 4;
    return (
      <GradeFormulario>
        {Array.from({ length: total }, (_, i) => (
          <Campo key={i}>
            <Skeleton.Input active size="small" style={{ width: 120, height: 16 }} />
            <Skeleton.Input active block style={{ height: 40 }} />
          </Campo>
        ))}
      </GradeFormulario>
    );
  }

  // tabela: cabeçalho + linhas com a mesma altura de célula da tabela real (48px)
  const total = linhas ?? 5;
  return (
    <GradeTabela>
      <Skeleton.Input active block style={{ height: 44 }} />
      {Array.from({ length: total }, (_, i) => (
        <Skeleton.Input key={i} active block style={{ height: 48 }} />
      ))}
    </GradeTabela>
  );
}

/* ------------------------------------------------------------------ */
/* Estilos                                                              */
/* ------------------------------------------------------------------ */

const ALTURA_MINIMA: Record<VarianteCarregando, string> = {
  bloco: '160px',
  pagina: '320px',
  tela: '100dvh',
};

const Container = styled.div<{ $variante: VarianteCarregando; $esqueleto: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $esqueleto }) => ($esqueleto ? 'stretch' : 'center')};
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  width: 100%;
  min-height: ${({ $variante, $esqueleto }) => ($esqueleto ? 'auto' : ALTURA_MINIMA[$variante])};
  padding: ${({ theme, $esqueleto }) => ($esqueleto ? '0' : theme.spacing.lg)};

  ${({ $variante, theme }) =>
    $variante === 'tela' &&
    `
      position: fixed;
      inset: 0;
      z-index: ${theme.zIndex.modal};
      background: rgba(255, 255, 255, 0.92);
    `}
`;

const Texto = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.textStyles.body.fontSize};
  line-height: ${({ theme }) => theme.textStyles.body.lineHeight};
  color: ${({ theme }) => theme.colors.gray[600]};
  text-align: center;
`;

const GradeTabela = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
`;

const GradeFormulario = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md};
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
    column-gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const Campo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;
